<script lang="ts" setup>
import { SancareBreadcrumb, SancarePagination, SancareToggle } from '@sancare/typescript-commons'
import { computed, h, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useBreadcrumb } from '@/breadcrumbs/useBreadcrumb'
import useDialysisMedicalUnitsSelection from '@/misc/composables/useDialysisMedicalUnitsSelection'
import { hasPermission, RoleEnum } from '@/misc/permissions/permissionsHelper'
import AdvancedStayListCriteria from '@/stay-list/AdvancedStayListCriteria.vue'
import useQuickMode from '@/stay-list/stay-list-table/line/composables/quickMode'
import McoStayListActions from '@/stay-list/stay-list-table/mco/McoStayListActions.vue'
import McoStayListTable from '@/stay-list/stay-list-table/mco/McoStayListTable.vue'
import { ListMcoStay } from '@/stay-list/stay-list-table/mco/types'
import SmrStayListActions from '@/stay-list/stay-list-table/smr/SmrStayListActions.vue'
import SmrStayListTable from '@/stay-list/stay-list-table/smr/SmrStayListTable.vue'
import StayQuickView from '@/stay-list/stay-quick-view/StayQuickView.vue'
import StayListCriteria from '@/stay-list/StayListCriteria.vue'
import { useStore } from '@/store'
import { ModeEnum, PmsiTypeEnum } from '@/store/modules/settings/types'

const store = useStore()
const route = useRoute()
const router = useRouter()

const props = defineProps<{
  pmsiType: PmsiTypeEnum
}>()

const StayListTables = {
  [PmsiTypeEnum.MCO]: McoStayListTable,
  [PmsiTypeEnum.SMR]: SmrStayListTable
}
const StayListTable = computed(() => StayListTables[props.pmsiType])
const StayListActionsComponents = {
  [PmsiTypeEnum.MCO]: McoStayListActions,
  [PmsiTypeEnum.SMR]: SmrStayListActions
}
const StayListActions = computed(() => StayListActionsComponents[props.pmsiType])
const isMcoPmsi = computed(() => props.pmsiType === PmsiTypeEnum.MCO)

const stayCount = computed(() => store.state.stayList.stayList.totalCount)
const pageCount = computed(() => store.state.stayList.stayList.pageCount)
const currentPage = computed(() => store.state.stayList.stayListParams.currentPage)
const mode = computed(() => store.state.stayList.stayListParams.mode)
const isSearchCreation = computed(() => store.state.stayList.stayListParams.searchCreation)
const isSearchEdition = computed(() => store.state.stayList.stayListParams.searchEdition !== -1)
const isSearchEditor = computed(() => hasPermission(RoleEnum.ROLE_SANCARE_SEARCH_MANAGER, store.state.login.userRole))
const isAdvancedSearchUser = computed(() => store.state.login.userRole === 'ROLE_DIM' || isSearchEditor.value)
const precision = computed(() => store.state.stayList.stayList.precision)
const recall = computed(() => store.state.stayList.stayList.recall)
const selected = computed(() => store.state.stayList.stayList.selected)
const totalMatching = computed(() => store.state.stayList.stayList.totalMatching)
const expected = computed(() => store.state.stayList.stayList.expected)
const params = computed(() => store.state.stayList.stayListParams)
const currentSearch = computed(() => store.state.stayList.currentSearch)
const teamMode = computed(() => store.state.login.currentUser.teamMode)
const userSettings = computed(() => store.state.settings.userSettings)
const { isQuickModeAvailable: isQuickModeAvailableForCountryAndMode, isQuickModeEnabled } = useQuickMode(mode)
const isQuickModeAvailable = computed(() => isMcoPmsi.value && isQuickModeAvailableForCountryAndMode.value)

const quickViewStay = ref<ListMcoStay | null>(null)
const isQuickViewOpen = ref(false)
const openQuickViewStayId = computed(() => (isQuickViewOpen.value ? quickViewStay.value?.id ?? null : null))
function openQuickView(stay: ListMcoStay): void {
  quickViewStay.value = stay
  isQuickViewOpen.value = true
  store.dispatch('mcoStay/markStaySeen', stay.id)
}

const { breadcrumbItems } = useBreadcrumb()

// Canonicalize the URL: sync ?staylistLabel=... with the current search being
// created/edited so the breadcrumb stays in sync without reading the store.
const searchLabel = computed((): string | null => {
  if (isSearchCreation.value) {
    return currentSearch.value.name || 'Nouvelle recherche'
  }
  if (isSearchEdition.value) {
    return currentSearch.value.name || null
  }
  return null
})
watch(searchLabel, (label) => {
  if (label && route.query.staylistLabel !== label) {
    router.replace({ query: { ...route.query, staylistLabel: label } })
  }
}, { immediate: true })

function setParameter(type: string, value: string | number): void {
  if (type === 'pageSize') {
    const newPageSize = Number(value)
    const newPageCount = Math.ceil(stayCount.value / newPageSize)

    // Stay on the same page if exists, else go to the last one
    const newCurrentPage = Math.min(currentPage.value, newPageCount || 1)

    store.commit('setStayListParam', { type: 'pageSize', value: newPageSize })
    store.commit('setStayListParam', { type: 'currentPage', value: newCurrentPage })

    store.dispatch('refreshStayList', { resetStaySelection: false })
    store.dispatch('refreshStayListUrl', {})
    return
  }

  store.commit('setStayListParam', { type, value })
  store.dispatch('refreshStayList', { resetStaySelection: type !== 'currentPage' })
  store.dispatch('refreshStayListUrl', {})
}


function setPage(value: string | number): void {
  setParameter('currentPage', value)
}

function setSearchIsAdvanced(isAdvanced: boolean): void {
  store.commit('setSearchIsAdvanced', isAdvanced)
}

function setQuickMode(enabled: boolean): void {
  const settings = { ...userSettings.value }
  settings.stayListQuickMode = enabled ? ['enabled'] : []
  store.dispatch('setUserSettings', settings)
}

function setPmsiType(pmsiType: PmsiTypeEnum): void {
  store.commit('setSearchPmsiType', pmsiType)
}

// mode lu dans l'URL (stayListParams pas encore initialisés) et réglages attendus pour ne pas vider la sélection
const isDialysisMode = computed(() => route.query.mode === ModeEnum.DIALYSIS
  && store.state.settings.sancareSettings.boolSettings !== undefined)
const { restrictSelectedMedicalUnitsToDialysis } = useDialysisMedicalUnitsSelection(isDialysisMode)
// immediate : avant le premier chargement lancé par initStayList
watch([isDialysisMode, () => store.getters.dialysisMedicalUnitIds], () => {
  if (restrictSelectedMedicalUnitsToDialysis() && store.state.stayList.initialized.stayList) {
    store.dispatch('refreshStayList', { resetStaySelection: true })
  }
}, { immediate: true })

watch(() => teamMode, (curr, prev) => {
  // FIXME: little hack that prevents refresh() to be called twice on browser refresh
  if (prev !== null) {
    store.dispatch('refreshStayList', { resetStaySelection: true, refreshUrl: false })
  }
})

onMounted(() => {
  if (isSearchCreation.value && props.pmsiType === PmsiTypeEnum.SMR) {
    setSearchIsAdvanced(true)
  }

  setPmsiType(props.pmsiType)
})
</script>

<template>
  <div class="container">
    <sancare-breadcrumb :items="breadcrumbItems" />
    <div class="row align-items-end">
      <h2 class="col-auto text-center">
        <span name="stayList_count">
          {{ stayCount }}{{ stayCount > 9999 ? '+' : '' }}
        </span>
        <div class="undertitle">
          <br v-if="isSearchEditor && params.mode !== ModeEnum.AUTOMATION">
          séjours
        </div>
      </h2>
      <template
        v-if="isMcoPmsi && isSearchEditor && params.mode !== ModeEnum.AUTOMATION"
      >
        <h2 class="col-auto text-center">
          <span
            v-if="precision !== null"
            name="precision_rate"
          >
            {{ precision }}%
          </span>
          <span
            v-else
            name="precision_rate"
          >
            --
          </span>
          <div class="undertitle">
            {{ selected ?? '--' }}/{{ totalMatching ?? '--' }}
          </div>
          <div class="undertitle">
            précision
          </div>
        </h2>
        <h2 class="col-auto text-center">
          <span
            v-if="recall !== null"
            name="recall_rate"
          >
            {{ recall }}%
          </span>
          <span
            v-else
            name="recall_rate"
          >
            --
          </span>
          <div class="undertitle">
            {{ selected ?? '--' }}/{{ expected ?? '--' }}
          </div>
          <div class="undertitle">
            rappel
          </div>
        </h2>
        <h2 class="col-auto ">
          <sancare-toggle
            class="px-5 py-1"
            :value="params.improveRecallMode"
            :disabled="mode !== ModeEnum.OPTIMIZABLE"
            @toggle-change="(improve) => setParameter('improveRecallMode', improve)"
          />
          <div class="undertitle">
            <br>
            Optimiser le rappel
          </div>
        </h2>
      </template>
      <template
        v-if="isAdvancedSearchUser && (isSearchCreation || isSearchEdition) && params.mode !== ModeEnum.AUTOMATION"
      >
        <h2 class="col-auto text-center">
          <v-dropdown
            :triggers="['hover']"
          >
            <sancare-toggle
              class="px-5 py-1"
              :value="currentSearch.isAdvancedSearch"
              :disabled="currentSearch.isAdvancedSearch"
              @toggle-change="(isAdvanced) => setSearchIsAdvanced(isAdvanced)"
            />
            <template #popper>
              <div>
                <div
                  v-if="!currentSearch.isAdvancedSearch"
                  class="text-center"
                >
                  Cette action est irréversible
                </div>
                Une recherche avancée ne peut-être convertie en recherche standard
              </div>
            </template>
          </v-dropdown>
          <div
            class="undertitle"
          >
            <br>Recherche avancée
          </div>
        </h2>
      </template>
      <h1 class="col-auto ms-auto">
        <div class="undertitle">
          Mode {{ $t('mode.' + params.mode) }}
        </div>
      </h1>
    </div>
    <advanced-stay-list-criteria
      v-if="currentSearch.isAdvancedSearch"
      :mode="mode"
      :pmsi-type="pmsiType"
      :is-quick-mode-available="isQuickModeAvailable"
      :is-quick-mode-enabled="isQuickModeEnabled"
      @set-stay-list-parameter="setParameter"
      @set-quick-mode="setQuickMode"
    />
    <stay-list-criteria
      v-else
      :mode="mode"
      :pmsi-type="pmsiType"
      :is-quick-mode-available="isQuickModeAvailable"
      :is-quick-mode-enabled="isQuickModeEnabled"
      @set-stay-list-parameter="setParameter"
      @set-quick-mode="setQuickMode"
    />
    <component
      :is="StayListActions"
      @set-stay-list-parameter="setParameter"
    />
    <component
      :is="StayListTable"
      :open-quick-view-stay-id="openQuickViewStayId"
      @open-quick-view="openQuickView"
    />
    <sancare-pagination
      :page-count="pageCount"
      :current-page="currentPage"
      @set-page="setPage"
    />
    <stay-quick-view
      v-model:open="isQuickViewOpen"
      :stay="quickViewStay"
      :mode="mode"
    />
  </div>
</template>
